import React, { useState, useEffect, useRef } from 'react'
import { Formik, Form, FormikProps } from 'formik'
import * as yup from 'yup'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { theme } from 'theme'
import { setAutoSubmissionStatus, getCampaignSummary, CampaignState } from 'state/campaigns'
import { selectCampaignSummary } from 'state'
import CampaignHeader from './CampaignHeader'
import { TooltipButton } from './style'
import Text from 'particles/Text'
import Toggle from 'particles/Toggle'
import Modal from 'particles/Modal'
import Icon from 'particles/Icon'
import styled from 'particles/themes/styled'
import InputField from 'components/Shared/Fields'

interface FormValues {
    auto_update_submission_status: boolean
    submission_status_filling_fast_min: number | null
    submission_status_full_min: number | null
    total_content_submissions: number
    slot_number: number
    batching_enable: boolean
}

interface UpdateAutoSubmissionStatusProps {
    className?: string
    campaign: CampaignState
}

const UpdateAutoSubmissionStatus: React.FC<UpdateAutoSubmissionStatusProps> = ({ className, campaign }) => {
    const [isOpen, setIsOpen] = useState(false)
    const formikRef = useRef<FormikProps<FormValues>>(null)
    const [t] = useTranslation()
    const dispatch = useDispatch()
    const isAutoUpdateSubmissionStatus = campaign.auto_update_submission_status
    const campaignSummary = useSelector(selectCampaignSummary(campaign.uuid))

    const handleConfirm = () => {
        if (formikRef.current) {
            formikRef.current.submitForm()
        }
    }

    useEffect(() => {
        if (isOpen) {
            dispatch(
                getCampaignSummary({
                    uuid: campaign.uuid,
                }),
            )
        }
    }, [isOpen])

    return (
        <>
            <TooltipButton
                p={0}
                m={0}
                width="100%"
                textProps={{ fontWeight: 'normal', color: theme.colors.black, textAlign: 'left' }}
                onClick={() => {
                    setIsOpen(true)
                }}
            >
                {t('button.setThresholdAutoUpdateStatus')}
            </TooltipButton>

            {isOpen && (
                <Modal
                    onClose={() => setIsOpen(false)}
                    size="m"
                    title={t('button.setThresholdAutoUpdateStatus')}
                    hasIcon={false}
                    primaryButtonProps={{
                        label: t('button.confirm'),
                    }}
                    secondaryButtonProps={{
                        label: t('button.cancel'),
                    }}
                    subtext={t('modalWindow.editThresholdAutoStatusUpdateSubtitle')}
                    onConfirm={handleConfirm}
                    isButtonSetFixed
                    className={className}
                >
                    <Formik
                        innerRef={formikRef}
                        enableReinitialize={true}
                        validationSchema={yup.object({
                            auto_update_submission_status: yup.boolean(),
                            submission_status_filling_fast_min: yup
                                .number()
                                .nullable()
                                .required(t('errorMessage.validation.enterNumberSlots'))
                                .positive(t('errorMessage.businessLogic.enterNonZeroSlots')),
                            submission_status_full_min: yup
                                .number()
                                .nullable()
                                .required(t('errorMessage.validation.enterNumberSlots'))
                                .positive(t('errorMessage.businessLogic.enterNonZeroSlots'))
                                .when(
                                    'submission_status_filling_fast_min',
                                    (submission_status_filling_fast_min: number, schema: any) =>
                                        submission_status_filling_fast_min
                                            ? schema.min(
                                                  submission_status_filling_fast_min + 1,
                                                  t('errorMessage.businessLogic.enterHigherNumberSubmissionFull'),
                                              )
                                            : schema,
                                ),
                            batching_enable: yup.boolean().nullable(),
                        })}
                        initialValues={{
                            auto_update_submission_status: isAutoUpdateSubmissionStatus,
                            submission_status_filling_fast_min: campaign.submission_status_filling_fast_min,
                            submission_status_full_min: campaign.submission_status_full_min,
                            total_content_submissions: campaignSummary
                                ? campaignSummary.total_content_submissions
                                : t('loadingState.loadingGenericPageSubtitle'),
                            slot_number: campaignSummary
                                ? campaignSummary.slot_number ?? '-'
                                : t('loadingState.loadingGenericPageSubtitle'),
                            batching_enable: campaign.batching_enable,
                        }}
                        onSubmit={(values: FormValues) => {
                            dispatch(
                                setAutoSubmissionStatus({
                                    isAutoUpdateSubmissionStatusEnabled: isAutoUpdateSubmissionStatus,
                                    uuid: campaign.uuid,
                                    full: values.submission_status_full_min,
                                    fillingFast: values.submission_status_filling_fast_min,
                                    isBatchingUpdated: values.batching_enable !== campaign.batching_enable,
                                    batchingEnable: values.batching_enable,
                                }),
                            )
                            setIsOpen(false)
                        }}
                    >
                        {({ values, setFieldValue }) => (
                            <Form>
                                <CampaignHeader campaign={campaign} />
                                <div className="description">
                                    <Text scale="bodyM700">
                                        {isAutoUpdateSubmissionStatus
                                            ? t('submission.autoUpdate.editNumberOfSubmissionHeader')
                                            : t('submission.autoUpdate.setNumberOfSubmissionHeader')}
                                    </Text>
                                    <Text scale="bodyM400">
                                        {t('submission.autoUpdate.editNumberOfSubmissionSubtext')}
                                    </Text>
                                    <div className="info">
                                        <Icon name="info" form="fill" />
                                        <Text scale="bodyM400">
                                            {t(
                                                'submission.autoUpdate.cantChangeStatusInstructon',
                                                'You cannot change the submission status manually after enabling auto-updates. To change submission status, edit the number of submissions above with new values.',
                                            )}
                                        </Text>
                                    </div>
                                </div>

                                <InputField.NumberInputField
                                    name="total_content_submissions"
                                    title={t('submission.autoUpdate.enterNumberOfSubmission')}
                                    isReadOnly
                                />
                                <InputField.NumberInputField
                                    isReadOnly
                                    name="slot_number"
                                    title={t('campaign.metric.enterCampaignSlotsHeader')}
                                />
                                <InputField.NumberInputField
                                    name="submission_status_full_min"
                                    title={t('campaign.metric.enterNumberSubmissionFullHeader')}
                                />
                                <InputField.NumberInputField
                                    name="submission_status_filling_fast_min"
                                    title={t('campaign.metric.enterNumberSubmissionFillingFastHeader')}
                                />
                                {values.batching_enable !== null && (
                                    <Toggle
                                        label={t(
                                            'submission.statusAutoUpdate.turnOnBatchSubmissionHeader',
                                        ).toUpperCase()}
                                        isChecked={values.batching_enable}
                                        handleChange={() => {
                                            const updateValue = !Boolean(values.batching_enable)
                                            setFieldValue('batching_enable', updateValue)
                                        }}
                                        subtext={
                                            campaign.registration_enable
                                                ? t(
                                                      'submission.statusAutoUpdate.batchSubmissionNotAvailableInstruction',
                                                  )
                                                : ''
                                        }
                                        isDisabled={campaign.registration_enable}
                                    />
                                )}

                                {values.batching_enable !== null ? (
                                    <div className="footer">
                                        {values.batching_enable ? (
                                            <div className="info">
                                                <Icon name="info" form="fill" />
                                                <Text scale="bodyM400">
                                                    {t('submission.statusAutoUpdate.turnOnBatchSubmissionInstruction')}
                                                </Text>
                                            </div>
                                        ) : (
                                            <Text scale="bodyM700">
                                                {t('submission.statusAutoUpdate.turnOffBatchSubmissionInstruction')}
                                            </Text>
                                        )}
                                        <Text>{t('submission.autoUpdate.turnOnBatchSubmissionWarning')}</Text>
                                    </div>
                                ) : null}
                            </Form>
                        )}
                    </Formik>
                </Modal>
            )}
        </>
    )
}

export default styled(UpdateAutoSubmissionStatus)`
    form {
        display: flex;
        flex-direction: column;
        gap: ${({ theme }) => theme.spacing[400]};
        .field-title {
            margin-bottom: 0px;
        }
    }
    .description {
        display: flex;
        flex-direction: column;
        gap: ${({ theme }) => theme.spacing[100]};
    }
    .info {
        display: flex;
        gap: ${({ theme }) => theme.spacing[100]};
        align-items: flex-start;
        svg {
            padding-top: 4px;
        }
    }
    .footer {
        display: flex;
        flex-direction: column;
        gap: ${({ theme }) => theme.spacing[200]};
    }
`
